"use client"

import type { VariantProps } from "class-variance-authority"
import type { HTMLMotionProps } from "motion/react"
import type { FC, ReactNode, SVGProps } from "react"
import { forwardRef, memo } from "react"
import { cva } from "class-variance-authority"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/browser"

export const badgeVariants = cva(
	"inline-flex flex-none items-center justify-center gap-1.5 rounded-md font-medium ring-1 ring-inset h-[1.4rem] min-w-[1.4rem]",
	{
		variants: {
			variant: {
				// https://tailwindui.com/components/application-ui/elements/badges
				default:
					"text-secondary bg-gray-50 dark:bg-gray-800 ring-black/10 dark:text-gray-300 dark:ring-white/10",
				"dark-gray":
					"bg-gray-200 text-gray-800 dark:bg-gray-700/70 dark:text-gray-300 ring-0",
				"light-gray":
					"bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300 ring-0",
				green:
					"bg-green-50 text-green-700 ring-green-500/40 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20",
				red: "bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20",
				blue: "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20",
				// A state that waits on someone, such as a draft awaiting review.
				amber:
					"bg-amber-50 text-amber-700 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20",
				important:
					"bg-white text-gray-500 ring-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:ring-white/10",
				glass:
					"bg-green-50/70 text-green-700 shadow-sm backdrop-blur-sm ring-green-500/30 " +
					"dark:bg-green-900/40 dark:text-green-400 dark:shadow-black/20 dark:ring-green-500/20",
				"nav-count":
					"bg-gray-500/15 text-primary ring-0 backdrop-blur-sm dark:bg-gray-400/15",
				transparent: "bg-transparent text-tertiary ring-0",
				// An option that exists but is off. Outline instead of ring so the box matches `default`.
				dashed:
					"bg-transparent text-tertiary ring-0 outline-dashed outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600",
				// A figure set inside running copy: a faint plate that lifts it without colour.
				stat: "bg-black/[0.03] text-primary ring-black/10 dark:bg-white/[0.05] dark:ring-white/10",
				// Sits on the amber impersonation bar.
				"on-amber": "bg-white/55 text-amber-900 ring-0",
				// Linear's mention chip: flat grey, no ring, text keeps the prose color.
				mention: "bg-gray-150 text-inherit ring-0 dark:bg-gray-750",
			},
			size: {
				default: "px-1.5 py-0.5 text-[0.7rem]",
				sm: "px-[0.4rem] h-5 py-0.5 text-xxs rounded-md",
				xs: "px-1 h-3.5 min-w-3.5 py-0.5 text-[10px] leading-none rounded-full",
				// A type, value, or HTTP method in an API reference.
				code: "h-5 min-w-0 rounded-[5px] px-1.5 py-0 font-mono text-[11.5px] font-normal leading-5",
				// Sits inside a line of prose, so it takes the surrounding type size.
				inline:
					"h-[1.125rem] min-w-0 max-w-full gap-1 rounded px-1 py-0 align-middle text-[length:inherit] font-normal leading-[1.125rem]",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

export interface BadgeProps
	extends HTMLMotionProps<"div">, VariantProps<typeof badgeVariants> {
	Icon?: LucideIcon
	RightIcon?: LucideIcon
	children: ReactNode
}

export const Badge = memo(
	forwardRef<HTMLDivElement, BadgeProps>(
		(
			{ className, size, variant, children, Icon, RightIcon, ...props },
			ref,
		) => {
			return (
				<motion.div
					ref={ref}
					{...props}
					className={cn(badgeVariants({ variant, size, className }))}
				>
					{Icon && <Icon className="h-3 min-h-3 w-3 min-w-3 opacity-50" />}
					{children}
					{RightIcon && (
						<RightIcon className="h-3 min-h-3 w-3 min-w-3 opacity-50" />
					)}
				</motion.div>
			)
		},
	),
)

export function NewBadge({ className }: { className?: string }) {
	return (
		<Badge variant="green" size="sm" className={className}>
			New
		</Badge>
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
